import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled, { ThemeProvider } from 'styled-components';
import { Head, Loader, Nav, Social, Email, Footer, KeyboardShortcuts } from '@components';
import { KeyboardShortcutsProvider } from '../hooks/KeyboardShortcutsContext';
import { GlobalStyle, theme } from '@styles';
import ScrollProgressBar from './scrollProgressBar';

const StyledContent = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  position: relative;

  #content {
    position: relative;
    z-index: 1;
  }
`;

const Layout = ({ children, location }) => {
  const isHome = location.pathname === '/';
  // The loader overlay stays mounted while it reveals the page, so the content mounts
  // (contentReady) a little before the loader unmounts (isLoading).
  const [isLoading, setIsLoading] = useState(isHome);
  const [contentReady, setContentReady] = useState(!isHome);

  // Sets target="_blank" rel="noopener noreferrer" on external links
  const handleExternalLinks = () => {
    const allLinks = Array.from(document.querySelectorAll('a'));
    if (allLinks.length > 0) {
      allLinks.forEach(link => {
        if (link.host !== window.location.host) {
          link.setAttribute('rel', 'noopener noreferrer');
          link.setAttribute('target', '_blank');
        }
      });
    }
  };

  useEffect(() => {
    if (!contentReady) {
      return;
    }

    if (location.hash) {
      const id = location.hash.substring(1); // location.hash without the '#'
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView();
          el.focus();
        }
      }, 0);
    }

    handleExternalLinks();
  }, [contentReady]);

  return (
    <>
      <Head />

      <div id="root">
        <ThemeProvider theme={theme}>
          <GlobalStyle />

          <a className="skip-to-content" href="#content">
            Skip to Content
          </a>

          {isLoading && isHome && (
            <Loader
              onReveal={() => setContentReady(true)}
              finishLoading={() => setIsLoading(false)}
            />
          )}

          {contentReady && (
            <KeyboardShortcutsProvider>
              <StyledContent>
                <Nav isHome={isHome} />
                <Social isHome={isHome} />
                <Email isHome={isHome} />
                <ScrollProgressBar />

                <div id="content">
                  {children}
                  <Footer />
                </div>
                <KeyboardShortcuts />
              </StyledContent>
            </KeyboardShortcutsProvider>
          )}
        </ThemeProvider>
      </div>
    </>
  );
};

Layout.propTypes = {
  children: PropTypes.node.isRequired,
  location: PropTypes.object.isRequired,
};

export default Layout;
